/**
 * Business Language — FASE 14 P1.
 * Architecture terms stay internal. UX speaks Spanish business language only.
 */

/** Canonical UX labels for architecture concepts. */
export const BUSINESS_LEXICON = {
  insights: "Oportunidades",
  goals: "Objetivos",
  mission: "Plan de trabajo",
  missions: "Planes de trabajo",
  tasks: "Acciones",
  task: "Acción",
  planning: "Planificación",
  approval: "Solicitud de aprobación",
  approvals: "Solicitudes de aprobación",
  knowledge: "Biblioteca",
  learning: "Aprendizajes",
  playbooks: "Guías de trabajo",
  playbook: "Guía de trabajo",
  pack: "Módulo activo",
  packs: "Módulos activos",
  store: "Extensiones",
  extensions: "Extensiones",
  health_score: "Estado del negocio",
  brief: "Resumen",
  needs_review: "Pendiente de revisión",
  proposal: "Propuesta",
  customerAi: "Asistente para clientes",
  smartBuyer: "Compras inteligentes",
  supplierAi: "Compras con Renata",
  customerConversations: "Conversaciones con clientes",
  usage: "Consumo IA",
  provider: "Proveedor IA",
  handoff: "Atención humana",
  bundles: "Packs",
  flowsEngine: "Motor de flujos",
  agents: "Especialistas",
  agent: "Especialista",
  collaboration: "Trabajo en equipo",
  memory: "Memoria del negocio",
  inbox: "Bandeja de captura",
  timeline: "Actividad",
  push: "Avisos push",
  chat: "Conversación",
  config: "Configuración",
  home: "Inicio",
  automations: "Automatizaciones",
  automation: "Automatización",
  skill: "Capacidad",
  skills: "Capacidades",
  run: "Ejecución",
  trigger: "Disparador",
  executor: "Ejecución",
  result: "Resultado",
  specialist: "Especialista",
  specialists: "Especialistas",
  collaboratesWith: "trabaja con",
  dependsOn: "depende de",
  fallbackTo: "alternativa",
  session_started: "Inicio de sesión",
  checkout_started: "Inicio de compra",
  proposal_created: "Propuesta creada",
  generated_by_agent: "Generado con ayuda de Renata",
  memory_miss: "Renata buscó información y no encontró una coincidencia",
  memory_hit: "Renata encontró información relevante",
} as const;

export type BusinessLexiconKey = keyof typeof BUSINESS_LEXICON;

/** Phrases Renata should use instead of architecture jargon. */
export const HUMAN_PHRASES = {
  created_mission: "Preparé un plan de trabajo.",
  created_tasks: "Preparé las acciones necesarias.",
  created_goal: "Definimos un objetivo.",
  created_insight: "Encontré una oportunidad para tu negocio.",
  created_playbook: "Armé una guía de trabajo.",
  consulted_knowledge: "Busqué la información en la documentación disponible.",
  learning_candidate: "Guardé un aprendizaje para que lo revises.",
  approval_needed: "Necesito tu OK antes de continuar.",
  rag_hit: "Encontré información relevante.",
  rag_miss: "No encontré información sobre eso.",
} as const;

const REPLACE_PAIRS: Array<[RegExp, string]> = [
  [/\bMissions?\b/gi, "planes de trabajo"],
  [/\bTasks?\b/gi, "acciones"],
  [/\bGoals?\b/gi, "objetivos"],
  [/\bInsights?\b/gi, "oportunidades"],
  [/\bPlaybooks?\b/gi, "guías de trabajo"],
  [/\bKnowledge(?:\s+Platform|\s+Layer)?\b/gi, "biblioteca"],
  [/\bLearning(?:\s+Platform)?\b/gi, "aprendizajes"],
  [/\bApproval(?:\s+Center|\s+Engine)?\b/gi, "solicitud de aprobación"],
  [/\bHandoff\b/gi, "atención humana"],
  [/\bPacks?\b/gi, "configuración del negocio"],
  [/\bPlanning\b/gi, "planificación"],
  [/\bAgents?\b/gi, "especialistas"],
  [/\bCollaboration\b/gi, "trabajo entre especialistas"],
  [/\bInbox\b/gi, "bandeja de captura"],
  [/\bPush Campaigns?\b/gi, "avisos push"],
  [/\bSkills?\b/gi, "capacidades"],
  [/\bGateway\b/gi, "servicio de IA"],
  [/\bLLM\b/g, "asistente"],
  [/\bPrompts?\b/gi, "instrucciones"],
  [/\bRAG\b/g, "búsqueda en documentación"],
  [/\bEmbeddings?\b/gi, "índice de búsqueda"],
  [/\bVectors?\b/gi, "índice"],
  [/\bChunks?\b/gi, "fragmentos"],
  [/\bProviders?\b/gi, "proveedor de IA"],
  [/\bTokens?\b/gi, "consumo"],
  [/\bWorkflows?\b/gi, "procesos"],
  [/\bMemory Engine\b/gi, "memoria del negocio"],
  [/\bHealth Score\b/gi, "estado del negocio"],
  [/\bNeeds Review\b/gi, "pendiente de revisión"],
  [/\bBrief\b/gi, "resumen"],
  [/\bDraft\b/gi, "borrador"],
  [/\bReview\b/gi, "revisión"],
  [/\bPending\b/gi, "pendiente"],
  [/\bRunning\b/gi, "en curso"],
  [/\bFailed\b/gi, "falló"],
  [/\bSuccess\b/gi, "listo"],
  [/\bCustomer AI\b/gi, "asistente para clientes"],
  [/\bMarketplace\b/gi, "tienda de extensiones"],
  [/\bOwner AI\b/gi, "centro owner"],
  [/\bExecutive AI\b/gi, "asesora de plataforma"],
  [/\bSmart Buyer\b/gi, "compras inteligentes"],
];

/** Rewrite assistant-facing text to business Spanish. Never exposes architecture. */
export function toBusinessLanguage(text: string): string {
  let out = text;
  for (const [re, rep] of REPLACE_PAIRS) {
    out = out.replace(re, rep);
  }
  return out;
}

export function businessLabel(key: BusinessLexiconKey | string): string {
  if (key in BUSINESS_LEXICON) {
    return BUSINESS_LEXICON[key as BusinessLexiconKey];
  }
  return toBusinessLanguage(String(key));
}
